export type TUserStatus = 'online' | 'away' | 'dnd' | 'offline'

/** What a user may pick: `online` means "back to automatic" */
export type TManualStatus = 'online' | 'dnd' | 'offline'

export type TPresenceActivity = 'active' | 'idle'

/**
 * Page / CRM object a user is looking at. No titles on purpose: the viewer resolves them
 * itself, so object level permissions are respected.
 */
export interface IPresenceLocation {
  path: string | null
  /** mobile screen key, always null for the dashboard */
  screen: string | null
  object: { scheme: string; uniqId: string } | null
}

/** State of THIS tab, reported through Ably presence */
export interface IPresenceTabState {
  activity: TPresenceActivity
  visible: boolean
  /** epoch milliseconds of the last user input */
  lastInputAt: number
  location: IPresenceLocation | null
}

/**
 * Presence data v1 as it goes over the wire (the same for web / iOS / Android).
 * snake_case by hand: the axios case middleware does not see Ably traffic.
 */
export interface IPresenceWireData {
  v: 1
  device: 'web'
  activity: TPresenceActivity
  visible: boolean
  last_input_at: number
  location: {
    path: string | null
    screen: string | null
    object: { scheme: string; uniq_id: string } | null
  } | null
}

export const toWireData = (state: IPresenceTabState): IPresenceWireData => ({
  v: 1,
  device: 'web',
  activity: state.activity,
  visible: state.visible,
  last_input_at: state.lastInputAt,
  location: state.location
    ? {
        path: state.location.path,
        screen: state.location.screen,
        object: state.location.object
          ? {
              scheme: state.location.object.scheme,
              uniq_id: state.location.object.uniqId,
            }
          : null,
      }
    : null,
})

const STATUS_RANK: Record<TUserStatus, number> = {
  offline: 0,
  dnd: 0,
  away: 1,
  online: 2,
}

/** Is `actual` a worse automatic status than `expected`? (used by the self-heal check) */
export const isWorseStatus = (actual: TUserStatus, expected: TUserStatus) =>
  STATUS_RANK[actual] < STATUS_RANK[expected]
